import { 
  collection, 
  addDoc, 
  getDocs, 
  query, 
  orderBy, 
  where, 
  Timestamp,
  updateDoc,
  doc,
  getDoc
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { TestResults, UserDetails } from '../types';

export interface FirebaseUser {
  id?: string;
  fullName: string;
  email: string;
  phone: string;
  businessType: string;
  role: string;
  country: string;
  state: string;
  createdAt: Timestamp;
  results?: TestResults;
}

export interface FirebaseAnalytics {
  totalUsers: number;
  completedTests: number;
  averageScore: number;
  readinessDistribution: {
    ready: number;
    almostReady: number;
    notReady: number;
  };
  lastUpdated: Timestamp;
}

// Save user data to Firebase
export const saveUserToFirebase = async (userDetails: UserDetails): Promise<string> => {
  try {
    const userDoc = {
      ...userDetails,
      createdAt: Timestamp.now()
    };
    
    const docRef = await addDoc(collection(db, 'users'), userDoc);
    return docRef.id;
  } catch (error) {
    console.error('Error saving user to Firebase:', error);
    throw error;
  }
};

// Save test results to Firebase
export const saveTestResultsToFirebase = async (
  userId: string, 
  results: TestResults
): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      results: results,
      completedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error saving test results to Firebase:', error);
    throw error;
  }
};

// Get all users from Firebase
export const getUsersFromFirebase = async (): Promise<FirebaseUser[]> => {
  try {
    const usersQuery = query(
      collection(db, 'users'),
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(usersQuery);
    const users: FirebaseUser[] = [];
    
    querySnapshot.forEach((doc) => {
      users.push({
        id: doc.id,
        ...doc.data()
      } as FirebaseUser);
    });
    
    return users;
  } catch (error) {
    console.error('Error fetching users from Firebase:', error);
    throw error;
  }
};

// Calculate and cache analytics
export const calculateAnalytics = async (): Promise<FirebaseAnalytics> => {
  try {
    const users = await getUsersFromFirebase();
    const completedUsers = users.filter(u => u.results);
    
    const totalUsers = users.length;
    const completedTests = completedUsers.length;
    
    const averageScore = completedUsers.length > 0 
      ? Math.round(completedUsers.reduce((sum, u) => sum + (u.results?.overallPercentage || 0), 0) / completedUsers.length)
      : 0;
    
    const readyCount = completedUsers.filter(u => u.results?.overallStatus === 'Ready').length;
    const almostReadyCount = completedUsers.filter(u => u.results?.overallStatus === 'Almost Ready').length;
    const notReadyCount = completedUsers.filter(u => u.results?.overallStatus === 'Not Ready').length;
    
    const analytics: FirebaseAnalytics = {
      totalUsers,
      completedTests,
      averageScore,
      readinessDistribution: {
        ready: completedTests > 0 ? Math.round((readyCount / completedTests) * 100) : 0,
        almostReady: completedTests > 0 ? Math.round((almostReadyCount / completedTests) * 100) : 0,
        notReady: completedTests > 0 ? Math.round((notReadyCount / completedTests) * 100) : 0
      },
      lastUpdated: Timestamp.now()
    };
    
    // Cache analytics in Firebase
    await addDoc(collection(db, 'analytics'), analytics);
    
    return analytics;
  } catch (error) {
    console.error('Error calculating analytics:', error);
    throw error;
  }
};

// Get location data for heatmap
export const getLocationData = async () => {
  try {
    const users = await getUsersFromFirebase();
    
    const locationMap = new Map();
    
    users.forEach(user => {
      const key = `${user.country}-${user.state}`;
      if (!locationMap.has(key)) {
        locationMap.set(key, {
          country: user.country,
          state: user.state,
          count: 0,
          totalScore: 0,
          completedCount: 0
        });
      }
      
      const location = locationMap.get(key);
      location.count++;
      
      if (user.results) {
        location.totalScore += user.results.overallPercentage;
        location.completedCount++;
      }
    });
    
    return Array.from(locationMap.values()).map(loc => ({
      country: loc.country,
      state: loc.state,
      count: loc.count,
      averageScore: loc.completedCount > 0 ? Math.round(loc.totalScore / loc.completedCount) : 0
    }));
  } catch (error) {
    console.error('Error getting location data:', error);
    throw error;
  }
};

// Get user growth data for charts
export const getUserGrowthData = async () => {
  try {
    const users = await getUsersFromFirebase();
    const completedUsers = users.filter(u => u.results);
    
    // Generate data for the last 30 days
    const growthData = Array.from({ length: 30 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - (29 - i));
      
      const usersOnDate = users.filter(u => {
        const userDate = u.createdAt.toDate();
        return userDate <= date;
      }).length;
      
      const completionsOnDate = completedUsers.filter(u => {
        if (!u.results) return false;
        const completionDate = new Date(u.results.completedAt);
        return completionDate <= date;
      }).length;
      
      return {
        date: date.toISOString().split('T')[0],
        users: usersOnDate,
        completions: completionsOnDate
      };
    });
    
    return growthData;
  } catch (error) {
    console.error('Error getting user growth data:', error);
    throw error;
  }
};

// Schedule export function (placeholder for actual implementation)
export const scheduleExport = async (
  frequency: 'weekly' | 'monthly',
  day: string,
  time: string
) => {
  try {
    const scheduleDoc = {
      frequency,
      day,
      time,
      email: 'admin@lsem.org.ng',
      active: true,
      createdAt: Timestamp.now()
    };
    
    await addDoc(collection(db, 'exportSchedules'), scheduleDoc);
    
    // In a real implementation, this would integrate with a job scheduler
    // like Google Cloud Scheduler or AWS EventBridge
    console.log('Export scheduled:', scheduleDoc);
    
    return true;
  } catch (error) {
    console.error('Error scheduling export:', error);
    throw error;
  }
};